import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { formatDate, formatDateTime } from "../../utils/formatDate";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomTypography from "../../globalStyles/fonts";
import { isMobile } from "../../globalStyles/mobile";
import { orderService } from "../../utils/services/orderService";
import { useDarkMode } from "../context/darkModeContext";

/**
 * Parcel weight for the Shipping-information panel.
 *
 * This used to read `order.shipment_weight`, which is not a field on the order
 * model and is written by nothing in the platform — so the row was always blank,
 * for every provider. The weight actually lives on the shipment subdoc: under
 * `packageData` when rl-tms-be builds it on a label buy, and at the top level
 * when a storefront provider supplies it (Pipe17 sends `weight`/`weightUnit` on
 * the fulfilment). Read both, preferring packageData to match the Dimension row
 * directly below.
 */
const shipmentWeight = (order) => {
  const s = order?.shipments?.[0];
  const weight = s?.packageData?.weight ?? s?.weight;
  if (weight === null || weight === undefined || weight === "") return "";
  // Providers report fractional weights at full float precision
  // (e.g. 9.25301204819277 lb) — two decimals is what an operator needs.
  const rounded = Number.isFinite(Number(weight))
    ? Number(Number(weight).toFixed(2))
    : weight;
  const unit = s?.packageData?.weight_unit ?? s?.weight_unit;
  return unit ? `${rounded} ${unit}` : `${rounded}`;
};

/**
 * Shipping cost for the Shipping-information panel.
 *
 * Two providers, two shapes, one row.
 *
 * Pipe17 states the carrier charge PER PARCEL, on the fulfilment
 * (`shippingCharge` -> `shipments[].shipping_price`) — never on the order:
 * Pipe17's own order-level `shippingPrice` is what the storefront charged the
 * buyer and is 0 on most orders that actually cost money to ship. Summed here
 * across the order's shipments, so a split order that went out as two parcels
 * shows what the order cost to ship in total. Blank until the order has a
 * fulfilment.
 *
 * Shopify has no per-parcel charge at all — a Shopify fulfillment carries no
 * cost field — so it keeps stating one number for the order in
 * `shipping_lines[]` (one entry per shipping method). That is the fallback,
 * and it is summed rather than listed, since two methods on one order once
 * rendered as "$5$7" with no separator.
 *
 * This used to read an order-level `shipping_price` first. That field is gone
 * (control-tower order.model.js): it was a sum of per-parcel charges that threw
 * away which parcel cost what. Summing here reproduces the same total from the
 * data that is still true at the parcel level.
 *
 * A shipment with no `shipping_price` contributes nothing rather than 0, and
 * when NO shipment states a charge we fall through to `shipping_lines` instead
 * of printing "$0".
 *
 * Returns "" — not "$0.00" — when neither source has anything, so "we were never
 * told" stays visually distinct from "shipping was free". A real 0 still prints.
 */
const shippingCost = (order) => {
  const charges = (order?.shipments || [])
    .map((s) => s?.shipping_price)
    .filter((v) => v !== null && v !== undefined && v !== "")
    .map(Number)
    .filter(Number.isFinite);
  if (charges.length) {
    const total = charges.reduce((sum, c) => sum + c, 0);
    // A float sum of currency values (13.8 + 24.08) lands on 37.879999999999995.
    const rounded = Number(total.toFixed(2));
    return `$${rounded.toLocaleString("en-US")}`;
  }
  const lines = order?.shipping_lines;
  if (!Array.isArray(lines) || lines.length === 0) return "";
  const total = lines.reduce((sum, x) => sum + (parseFloat(x?.price) || 0), 0);
  return Number.isFinite(total) ? `$${total.toLocaleString("en-US")}` : "";
};

/**
 * Parcel dimensions for the Shipping-information panel, as "L x W x H (unit)".
 *
 * Returns "" when the shipment carries no dimensions at all. This used to
 * coerce each missing side to 0 and print "0 x 0 x 0 (undefined)" for any order
 * with a shipment but no measured parcel — which reads as a real measurement of
 * zero rather than as "not recorded". Most storefront-fulfilled orders are in
 * that state: the carrier reports weight but not dimensions.
 *
 * `packageData.dimension` (rl-tms-be's shape) spells the unit `height_unit`,
 * while the order model's own `shipmentSchema.dimension` spells it `unit` —
 * accept either, and drop the parenthesis entirely when neither is set.
 */
const shipmentDimension = (order) => {
  const s = order?.shipments?.[0];
  const d = s?.packageData?.dimension ?? s?.dimension;
  const sides = [d?.length, d?.width, d?.height];
  // Nothing measured -> render nothing. A genuine 0 on one side still counts as
  // measured, so check for null/undefined rather than falsiness.
  if (sides.every((v) => v === null || v === undefined || v === "")) return "";

  const fmt = (v) =>
    v === null || v === undefined || v === "" || !Number.isFinite(Number(v))
      ? "-"
      : Number(Number(v).toFixed(2));
  const unit = d?.height_unit ?? d?.unit;
  return `${fmt(sides[0])} x ${fmt(sides[1])} x ${fmt(sides[2])}${
    unit ? ` (${unit})` : ""
  }`;
};

const OrderDetailsModal = ({ open, onClose, order, edit, fetchOrderData }) => {
  const [editingItem, setEditingItem] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchedOrder, setFetchedOrder] = useState(null);
  const [showSensitive, setShowSensitive] = useState(true);
  const navigate = useNavigate();
  const { colors } = useDarkMode();

  const sensitiveStyle = (extra = {}) =>
    showSensitive
      ? extra
      : {
          ...extra,
          filter: "blur(5px)",
          userSelect: "none",
          WebkitUserSelect: "none",
        };

  // Province/zip are not personally identifying, so keep them readable even when blurred.
  const renderAddress = (address) => {
    const blurredParts = [
      address?.name,
      address?.address1,
      address?.address2,
      address?.city,
      address?.country,
    ].filter(Boolean);
    const visibleParts = [address?.province, address?.zip].filter(Boolean);
    return (
      <>
        {blurredParts.length > 0 && (
          <span style={sensitiveStyle()}>{blurredParts.join(", ")}</span>
        )}
        {blurredParts.length > 0 && visibleParts.length > 0 ? ", " : ""}
        {visibleParts.join(", ")}
      </>
    );
  };

  // Fetch order by ID when modal opens
  useEffect(() => {
    if (open && order?._id) {
      const id = order._id;
      setLoading(true);
      orderService
        .get(id)
        .then((res) => {
          setFetchedOrder(res);
        })
        .catch((err) => {
          console.error("Failed to fetch order:", err);
          setFetchedOrder(order || null);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, order?._id]);

  // Use fetchedOrder for display, fallback to order prop
  const displayOrder = fetchedOrder || order;
  if (!displayOrder) return null;

  // Distinct warehouses the order actually shipped from, one per shipment.
  // `api/order`'s get hook populates `shipments.wms_provider`, so this is the
  // warehouse document; we label it with its own `name` because every Pipe17
  // location shares the `rl` WMS provider and the provider name would read
  // "rl" for all of them. Falls back to an empty list when the field is a bare
  // id (populate unavailable) rather than printing an ObjectId.
  const shipmentWarehouses = [
    ...new Set(
      (fetchedOrder?.shipments || [])
        .map((s) => s?.wms_provider?.name)
        .filter(Boolean),
    ),
  ];

  // Distinct carrier services across the order's shipments, for the Last Mile
  // Delivery row. Deduped because a multi-parcel order normally ships every box
  // on the same service, and repeating it once per parcel says nothing.
  //
  // `service` is the specific service level (e.g. "Ground"); `provider` is the
  // carrier itself (e.g. "FedEx"), which is all the Shopify path and
  // control-tower's own TMS pipeline record. Fall back to it rather than
  // leaving the row blank — the order-level `lastMileDelivery` that used to
  // back-stop this is gone (control-tower order.model.js), since one field
  // could not describe an order whose two parcels moved on two carriers.
  const shipmentsForDisplay = fetchedOrder?.shipments || [];
  const distinct = (key) => [
    ...new Set(shipmentsForDisplay.map((s) => s?.[key]).filter(Boolean)),
  ];
  const lastMileServices = distinct("service");
  const lastMileCarriers = distinct("provider");
  const lastMile = lastMileServices.length
    ? lastMileServices
    : lastMileCarriers;

  // Tracking numbers paired with the carrier's own tracking page, deduped by
  // number (a split order repeats neither, but the same parcel can appear on
  // two fulfilment updates). `tracking_url` is what the storefront provider
  // sent alongside the number — Pipe17 `trackingURLs`, Shopify's fulfilment
  // `tracking_url` — and is absent on TMS-generated shipments, which fall back
  // to the internal history page.
  //
  // Only http(s) is treated as a link: the value is third-party data landing in
  // an href, and a `javascript:` URL there would run on click.
  const trackingLinks = [
    ...(fetchedOrder?.shipments || [])
      .filter((s) => s?.tracking_number)
      .reduce((acc, s) => {
        const url = /^https?:\/\//i.test(s.tracking_url || "")
          ? s.tracking_url
          : "";
        // First non-empty URL wins, so a later untracked duplicate cannot
        // downgrade a number that already has a carrier page.
        if (!acc.has(s.tracking_number) || (url && !acc.get(s.tracking_number)))
          acc.set(s.tracking_number, url);
        return acc;
      }, new Map()),
  ].map(([tracking_number, tracking_url]) => ({
    tracking_number,
    tracking_url,
  }));

  // Fallback destination when the carrier gave us no tracking page. Takes the
  // clicked number rather than `shipments[0]`, which is what the old handler
  // sent for every row of a multi-parcel order.
  const openTrackingHistory = (trackingNumber) => {
    const queryParams = new URLSearchParams({
      status: `${trackingNumber}`,
    }).toString();
    const path = `/order-tracking-history/${displayOrder._id}?${queryParams}`;
    localStorage.setItem("path", path);
    navigate(path);
  };

  const hasSensitiveData = Boolean(
    displayOrder?.customer?.first_name ||
    displayOrder?.customer?.last_name ||
    fetchedOrder?.customer?.email ||
    fetchedOrder?.customer?.phone ||
    fetchedOrder?.billing_address?.name,
  );

  const handleEditClick = (index, field) => {
    if (!displayOrder?.line_items || !displayOrder.line_items[index]) return;
    setEditingItem({ index, field });
    setEditedValues({
      ...editedValues,
      [field]: displayOrder.line_items[index][field],
    });
  };

  const handleInputChange = (field, value) => {
    setEditedValues({
      ...editedValues,
      [field]: value,
    });
  };

  const handleSave = async (index) => {
    if (!displayOrder?.line_items || !displayOrder.line_items[index]) return;
    try {
      const updatedItem = {
        ...displayOrder.line_items[index],
        ...editedValues,
      };
      const updatedOrder = {
        ...displayOrder,
        line_items: [
          ...displayOrder.line_items.slice(0, index),
          updatedItem,
          ...displayOrder.line_items.slice(index + 1),
        ],
      };
      const res = await orderService.edit(updatedOrder, displayOrder._id);
      if (res?._id) {
        console.log(res);
        fetchOrderData();
      }
      setEditingItem(null);
      onClose();
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  const handleDelete = async (index) => {
    if (!displayOrder?.line_items || !displayOrder.line_items[index]) return;
    try {
      const updatedOrder = {
        ...displayOrder,
        line_items: [
          ...displayOrder.line_items.slice(0, index),
          ...displayOrder.line_items.slice(index + 1),
        ],
      };
      await orderService.edit(updatedOrder, displayOrder._id);
      onClose();
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  };

  const formattedDate = formatDateTime(displayOrder?.order_date, "");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <div
        style={{
          justifyContent: "space-between",
          display: "flex",
          borderWidth: 1,
          borderColor: colors.borderLight,
          alignItems: "center",
          padding: 10,
        }}
      >
        <CustomTypography
          fontSize={20}
          fontWeight={500}
          color={colors.textPrimary}
        >
          Order Details
        </CustomTypography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: colors.textPrimary,
          }}
        >
          <CloseIcon />
        </IconButton>
      </div>
      <Divider />

      <DialogContent
        sx={{
          overflowY: "auto",
          "&::WebkitScrollbar": {
            width: "5px",
          },
          "&::WebkitScrollbar-track": {
            backgroundColor: "#D9D9D9",
            borderRadius: 14,
          },
          "&::WebkitScrollbar-thumb": {
            backgroundColor: "#273894",
            borderRadius: "6px",
            "&:hover": {
              backgroundColor: "#555",
            },
          },
        }}
      >
        {loading ? (
          <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Header skeleton */}
            <Box>
              <Skeleton variant="text" width="25%" height={32} sx={{ mb: 1 }} />
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Skeleton variant="rounded" width={120} height={32} />
                <Skeleton variant="rounded" width={180} height={32} />
              </Box>
            </Box>

            {/* Two column layout skeleton */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 3,
              }}
            >
              {/* Customer & Order Card */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.bgPaper,
                }}
              >
                <Skeleton
                  variant="text"
                  width="40%"
                  height={24}
                  sx={{ mb: 2 }}
                />
                {[1, 2, 3, 4].map((i) => (
                  <Box key={i} sx={{ mb: 2 }}>
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={16}
                      sx={{ mb: 0.5 }}
                    />
                    <Skeleton variant="text" width="100%" height={20} />
                  </Box>
                ))}
              </Box>

              {/* Shipping Information Card */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.bgPaper,
                }}
              >
                <Skeleton
                  variant="text"
                  width="40%"
                  height={24}
                  sx={{ mb: 2 }}
                />
                {[1, 2, 3, 4].map((i) => (
                  <Box key={i} sx={{ mb: 2 }}>
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={16}
                      sx={{ mb: 0.5 }}
                    />
                    <Skeleton variant="text" width="100%" height={20} />
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Line Items table skeleton */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.bgPaper,
              }}
            >
              <Skeleton variant="text" width="30%" height={24} sx={{ mb: 2 }} />
              {[1, 2, 3].map((i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={50}
                  sx={{ mb: 1 }}
                />
              ))}
            </Box>
          </Box>
        ) : (
          <>
            {/* Header Section */}
            <Box
              sx={{
                p: 4,
                borderBottom: `2px solid ${colors.border}`,
                mb: 3,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <CustomTypography
                  fontSize={18}
                  fontWeight={700}
                  color={colors.textPrimary}
                >
                  Order #{displayOrder?.order_id}
                </CustomTypography>
                <Box
                  sx={{
                    backgroundColor: "#FFF3CF",
                    border: "1px solid #FF8A00B3",
                    borderRadius: "20px",
                    px: 1.5,
                    py: 0.5,
                    display: "inline-flex",
                  }}
                >
                  <Typography color={"#FF8A00"} fontSize={12} fontWeight={600}>
                    {displayOrder?.status}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    backgroundColor: colors.optionBg,
                    borderRadius: 1,
                  }}
                >
                  <CustomTypography fontSize={11} color={colors.textTertiary}>
                    Placed on
                  </CustomTypography>
                  <CustomTypography
                    fontSize={13}
                    fontWeight={600}
                    color={colors.textPrimary}
                  >
                    {formattedDate}
                  </CustomTypography>
                </Box>
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    backgroundColor: "#007DFF15",
                    borderRadius: 1,
                  }}
                >
                  <CustomTypography fontSize={11} color={colors.textTertiary}>
                    Updated on
                  </CustomTypography>
                  <CustomTypography
                    fontSize={13}
                    fontWeight={600}
                    color="#007DFF"
                  >
                    {formatDateTime(displayOrder?.updatedAt, "N/A")}
                  </CustomTypography>
                </Box>
              </Box>
            </Box>

            {/* Two Column Layout */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 3,
                px: 3,
                mb: 3,
              }}
            >
              <Box
                sx={{
                  backgroundColor: colors.bgPaper,
                  borderRadius: 1.5,
                  minHeight: 400,
                  border: `1px solid ${colors.border}`,
                  p: 2.5,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <CustomTypography
                      fontSize={18}
                      fontWeight={500}
                      color={colors.textSecondary}
                    >
                      Customer & Order
                    </CustomTypography>
                    {/* {hasSensitiveData && (
                      <IconButton
                        size="small"
                        aria-label={
                          showSensitive
                            ? "Hide sensitive information"
                            : "Show sensitive information"
                        }
                        onClick={() => setShowSensitive((prev) => !prev)}
                        sx={{ color: colors.textSecondary }}
                      >
                        {showSensitive ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    )} */}
                  </Box>
                  <Box mt={2}>
                    <Grid container spacing={2}>
                      {/* <Grid item xs={5}>
                        <Typography
                          fontSize={14}
                          fontWeight={400}
                          color={colors.textSecondary}
                          fontFamily={"Poppins"}
                        >
                          Name
                        </Typography>
                      </Grid>
                      <Grid item xs={7} container alignItems="flex-start">
                        <Grid item>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                          >
                            :
                          </Typography>
                        </Grid>
                        <Grid item xs>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                            style={sensitiveStyle({
                              marginLeft: 5,
                              wordBreak: "break-word",
                            })}
                          >
                            {displayOrder.customer?.first_name}{" "}
                            {displayOrder.customer?.last_name}
                          </Typography>
                        </Grid>
                      </Grid> 
                      <Grid item xs={5}>
                        <Typography
                          fontSize={14}
                          fontWeight={400}
                          color={colors.textSecondary}
                          fontFamily={"Poppins"}
                        >
                          Email
                        </Typography>
                      </Grid>
                      <Grid item xs={7} container alignItems="flex-start">
                        <Grid item>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                          >
                            :
                          </Typography>
                        </Grid>
                        <Grid item xs>
                          {" "}
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                            style={sensitiveStyle({
                              marginLeft: 5,
                              wordBreak: "break-word",
                            })}
                          >
                            {fetchedOrder?.customer?.email || ""}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography
                          fontSize={14}
                          fontWeight={400}
                          color={colors.textSecondary}
                          fontFamily={"Poppins"}
                        >
                          Phone No
                        </Typography>
                      </Grid>
                      <Grid item xs={7} container alignItems="flex-start">
                        <Grid item>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                          >
                            :
                          </Typography>
                        </Grid>
                        <Grid item xs>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                            style={sensitiveStyle({ marginLeft: 5 })}
                          >
                            {fetchedOrder?.customer?.phone || ""}
                          </Typography>
                        </Grid>
                      </Grid>*/}
                      <Grid item xs={5}>
                        <Typography
                          fontSize={14}
                          fontWeight={400}
                          color={colors.textSecondary}
                          fontFamily={"Poppins"}
                        >
                          Payment Method
                        </Typography>
                      </Grid>
                      <Grid item xs={7} container alignItems="flex-start">
                        <Grid item>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                          >
                            :
                          </Typography>
                        </Grid>
                        <Grid item xs>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                            style={{ marginLeft: 5 }}
                          >
                            {fetchedOrder?.payment_method || ""}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography
                          fontSize={14}
                          fontWeight={400}
                          color={colors.textSecondary}
                          fontFamily={"Poppins"}
                        >
                          Total Bill
                        </Typography>
                      </Grid>
                      <Grid item xs={7} container alignItems="flex-start">
                        <Grid item>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                          >
                            :
                          </Typography>
                        </Grid>
                        <Grid item xs>
                          {" "}
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                            style={{ marginLeft: 5 }}
                          >
                            {fetchedOrder?.total_price
                              ? `$${parseFloat(fetchedOrder.total_price).toLocaleString("en-US")}`
                              : ""}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography
                          fontSize={14}
                          fontWeight={400}
                          color={colors.textSecondary}
                          fontFamily={"Poppins"}
                        >
                          Billing Address
                        </Typography>
                      </Grid>
                      <Grid item xs={7} container alignItems="flex-start">
                        <Grid item>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                          >
                            :
                          </Typography>
                        </Grid>
                        <Grid item xs>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                            style={{ marginLeft: 5 }}
                          >
                            <Typography
                              fontSize={14}
                              fontWeight={400}
                              color={colors.textSecondary}
                              fontFamily={"Poppins"}
                              style={{ marginLeft: 4 }}
                            >
                              {renderAddress(fetchedOrder?.billing_address)}
                            </Typography>
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography
                          fontSize={14}
                          fontWeight={400}
                          color={colors.textSecondary}
                          fontFamily={"Poppins"}
                        >
                          Shipping Address
                        </Typography>
                      </Grid>
                      <Grid item xs={7} container alignItems="flex-start">
                        <Grid item>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                          >
                            :
                          </Typography>
                        </Grid>
                        <Grid item xs>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                            style={{ marginLeft: 4 }}
                          >
                            {renderAddress(fetchedOrder?.shipping_address)}
                          </Typography>
                        </Grid>
                      </Grid>
                      {/* Warehouse now lives in the Shipping information panel,
                          below Shipping Cost. */}
                      {/* Provider-supplied order tags (Pipe17 sends an array,
                          Shopify a comma-separated string; both are normalized
                          to a deduped string[] before they reach us). Hidden
                          entirely when the order has none, which is the case
                          for every order created before tags were persisted. */}
                      {displayOrder?.tags?.length > 0 && (
                        <>
                          <Grid item xs={5}>
                            <Typography
                              fontSize={14}
                              fontWeight={400}
                              color={colors.textSecondary}
                              fontFamily={"Poppins"}
                            >
                              Tags
                            </Typography>
                          </Grid>
                          <Grid item xs={7} container alignItems="flex-start">
                            <Grid item>
                              <Typography
                                fontSize={14}
                                fontWeight={400}
                                color={colors.textSecondary}
                                fontFamily={"Poppins"}
                              >
                                :
                              </Typography>
                            </Grid>
                            <Grid item xs>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                  marginLeft: "5px",
                                }}
                              >
                                {displayOrder.tags.map((tag) => (
                                  <Chip
                                    key={tag}
                                    label={tag}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      height: 22,
                                      maxWidth: "100%",
                                      borderColor: colors.border,
                                      "& .MuiChip-label": {
                                        fontSize: 12,
                                        fontFamily: "Poppins",
                                        color: colors.textSecondary,
                                        paddingInline: "8px",
                                      },
                                    }}
                                  />
                                ))}
                              </Box>
                            </Grid>
                          </Grid>
                        </>
                      )}
                      {/* Pipe17 order exceptions — Pipe17's own "why is this
                          order stuck" signal (eRouteOutOfStock, eInvalidAddress,
                          eShippingMethod, ...). ACTIVE ones only: Pipe17 never
                          deletes a dismissed exception, so the provider filters
                          them out before they are stored. Hidden entirely when
                          the order has none, which covers every non-Pipe17
                          order. Display-only — an exception deliberately does
                          not drive status or holds. */}
                      {displayOrder?.exceptions?.length > 0 && (
                        <>
                          <Grid item xs={5}>
                            <Typography
                              fontSize={14}
                              fontWeight={400}
                              color={colors.textSecondary}
                              fontFamily={"Poppins"}
                            >
                              Exceptions
                            </Typography>
                          </Grid>
                          <Grid item xs={7} container alignItems="flex-start">
                            <Grid item>
                              <Typography
                                fontSize={14}
                                fontWeight={400}
                                color={colors.textSecondary}
                                fontFamily={"Poppins"}
                              >
                                :
                              </Typography>
                            </Grid>
                            <Grid item xs>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                  marginLeft: "5px",
                                }}
                              >
                                {displayOrder.exceptions.map((exc, i) => {
                                  // `details` is already the human sentence —
                                  // the provider digs it out of the JSON blob
                                  // Pipe17 sends for about half of them.
                                  const label = exc?.type || "Exception";
                                  const tip = [exc?.details, exc?.severity && `severity: ${exc.severity}`]
                                    .filter(Boolean)
                                    .join(" — ");
                                  return (
                                    <Tooltip
                                      key={exc?.exception_id || `${label}-${i}`}
                                      title={tip || label}
                                      arrow
                                    >
                                      <Chip
                                        label={label}
                                        size="small"
                                        variant="outlined"
                                        color={
                                          exc?.severity === "high"
                                            ? "error"
                                            : "warning"
                                        }
                                        sx={{
                                          height: 22,
                                          maxWidth: "100%",
                                          "& .MuiChip-label": {
                                            fontSize: 12,
                                            fontFamily: "Poppins",
                                            paddingInline: "8px",
                                          },
                                        }}
                                      />
                                    </Tooltip>
                                  );
                                })}
                              </Box>
                            </Grid>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Box>
                </Box>
              </Box>

              {/* Shipping Information Card */}
              <Box
                sx={{
                  backgroundColor: colors.bgPaper,
                  borderRadius: 1.5,
                  minHeight: 400,
                  border: `1px solid ${colors.border}`,
                  p: 2.5,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <CustomTypography
                    fontSize={18}
                    fontWeight={500}
                    color={colors.textSecondary}
                  >
                    Shipping information
                  </CustomTypography>
                  <Box mt={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={5}>
                        <Typography
                          fontSize={14}
                          fontWeight={400}
                          color={colors.textSecondary}
                          fontFamily={"Poppins"}
                        >
                          Last Mile Delivery
                        </Typography>
                      </Grid>
                      <Grid item xs={7} container alignItems="flex-start">
                        <Grid item>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                          >
                            :
                          </Typography>
                        </Grid>
                        <Grid item xs>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                            style={{ marginLeft: 5 }}
                          >
                            {/* The carrier the parcel actually goes out on,
                                read off the shipments — one line per distinct
                                value, since a split order can move on more
                                than one. Prefers the service level and falls
                                back to the carrier name; see `lastMile`. */}
                            {lastMile.map((value) => (
                              <div key={value}>{value}</div>
                            ))}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography
                          fontSize={14}
                          fontWeight={400}
                          color={colors.textSecondary}
                          fontFamily={"Poppins"}
                        >
                          Weight
                        </Typography>
                      </Grid>
                      <Grid item xs={7} container alignItems="flex-start">
                        <Grid item>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                          >
                            :
                          </Typography>
                        </Grid>
                        <Grid item xs>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                            style={{ marginLeft: 5 }}
                          >
                            {shipmentWeight(fetchedOrder)}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography
                          fontSize={14}
                          fontWeight={400}
                          color={colors.textSecondary}
                          fontFamily={"Poppins"}
                        >
                          Dimension
                        </Typography>
                      </Grid>
                      <Grid item xs={7} container alignItems="flex-start">
                        <Grid item>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                          >
                            :
                          </Typography>
                        </Grid>
                        <Grid item xs>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                            style={{ marginLeft: 5 }}
                          >
                            {shipmentDimension(fetchedOrder)}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography
                          fontSize={14}
                          fontWeight={400}
                          color={colors.textSecondary}
                          fontFamily={"Poppins"}
                        >
                          Tracking Number
                        </Typography>
                      </Grid>
                      <Grid item xs={7} container alignItems="flex-start">
                        <Grid item>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                          >
                            :
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs
                          sx={{ display: "flex", flexDirection: "column" }}
                        >
                          {trackingLinks.map(
                            ({ tracking_number, tracking_url }) => {
                              const label = (
                                <Typography
                                  fontSize={14}
                                  fontWeight={400}
                                  color={colors.textSecondary}
                                  fontFamily={"Poppins"}
                                  style={{
                                    marginLeft: 5,
                                    color: "blue",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {tracking_number}
                                </Typography>
                              );
                              // Carrier's own page when we have it, in a new
                              // tab so the order stays open behind it.
                              return tracking_url ? (
                                <Link
                                  key={tracking_number}
                                  href={tracking_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ cursor: "pointer" }}
                                >
                                  {label}
                                </Link>
                              ) : (
                                <Link
                                  key={tracking_number}
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    openTrackingHistory(tracking_number)
                                  }
                                >
                                  {label}
                                </Link>
                              );
                            },
                          )}
                        </Grid>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography
                          fontSize={14}
                          fontWeight={400}
                          color={colors.textSecondary}
                          fontFamily={"Poppins"}
                        >
                          Expected Delivery
                        </Typography>
                      </Grid>
                      <Grid item xs={7} container alignItems="flex-start">
                        <Grid item>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                          >
                            :
                          </Typography>
                        </Grid>
                        <Grid item xs>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                            style={{ marginLeft: 5 }}
                          >
                            {/* The marketplace's deliver-by date, as the
                                provider stated it (control-tower
                                order.model.js expected_delivery_date). Blank
                                for providers that send none. */}
                            {formatDate(displayOrder?.expected_delivery_date, "")}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography
                          fontSize={14}
                          fontWeight={400}
                          color={colors.textSecondary}
                          fontFamily={"Poppins"}
                        >
                          Shipping Cost
                        </Typography>
                      </Grid>
                      <Grid item xs={7} container alignItems="flex-start">
                        <Grid item>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                          >
                            :
                          </Typography>
                        </Grid>
                        <Grid item xs>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            color={colors.textSecondary}
                            fontFamily={"Poppins"}
                            style={{ marginLeft: 5 }}
                          >
                            {shippingCost(fetchedOrder)}
                          </Typography>
                        </Grid>
                      </Grid>
                      {/* Warehouse the order actually shipped from, one line
                          per distinct value.

                          Read only from the shipments: the order-level
                          `warehouse` field this also used to check is gone
                          (control-tower order.model.js), because it was only
                          ever populated when every parcel agreed — exactly the
                          case where it duplicated the shipments — and was blank
                          on the split orders where an order-level answer would
                          have been worth something.

                          Uses the warehouse's own `name`, populated by
                          `api/order`'s get hook: every Pipe17 location shares
                          the `rl` WMS provider, so a provider name would render
                          "rl" for all of them. */}
                      {shipmentWarehouses.length > 0 && (
                        <>
                          <Grid item xs={5}>
                            <Typography
                              fontSize={14}
                              fontWeight={400}
                              color={colors.textSecondary}
                              fontFamily={"Poppins"}
                            >
                              Warehouse
                            </Typography>
                          </Grid>
                          <Grid item xs={7} container alignItems="flex-start">
                            <Grid item>
                              <Typography
                                fontSize={14}
                                fontWeight={400}
                                color={colors.textSecondary}
                                fontFamily={"Poppins"}
                              >
                                :
                              </Typography>
                            </Grid>
                            <Grid item xs>
                              <Typography
                                fontSize={14}
                                fontWeight={400}
                                color={colors.textSecondary}
                                fontFamily={"Poppins"}
                                style={{ marginLeft: 5 }}
                              >
                                {shipmentWarehouses.map((w) => (
                                  <div key={w}>{w}</div>
                                ))}
                              </Typography>
                            </Grid>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Line Items Section */}
            <Box
              sx={{
                border: `1px solid ${colors.border}`,
                borderRadius: 1.5,
                p: 2.5,
                mb: 3,
                mx: 3,
              }}
            >
              <CustomTypography
                fontSize={18}
                fontWeight={500}
                color={colors.textSecondary}
              >
                ITEMS ORDERED
              </CustomTypography>
              <TableContainer
                sx={{
                  maxHeight: "400px",
                  overflowY: "auto",
                  "&::WebkitScrollbar": {
                    width: "5px",
                  },
                  "&::WebkitScrollbar-track": {
                    backgroundColor: "#D9D9D9",
                    borderRadius: 14,
                  },
                  "&::WebkitScrollbar-thumb": {
                    backgroundColor: "#273894",
                    borderRadius: "6px",
                    "&:hover": {
                      backgroundColor: "#555",
                    },
                  },
                  marginTop: 2,
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        style={{
                          backgroundColor: colors.tableHeader,
                          borderRight: `1px solid ${colors.border}`,
                          width: "25%",
                          border: `1px solid ${colors.border}`,
                          borderRadius: 0,
                        }}
                      >
                        <CustomTypography fontSize={14}>
                          Product Name
                        </CustomTypography>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: colors.tableHeader,
                          borderRight: `1px solid ${colors.border}`,
                          width: "25%",
                          border: `1px solid ${colors.border}`,
                        }}
                      >
                        <CustomTypography fontSize={14}>
                          Quantity
                        </CustomTypography>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: colors.tableHeader,
                          borderRight: `1px solid ${colors.border}`,
                          width: "25%",
                          border: `1px solid ${colors.border}`,
                        }}
                      >
                        <CustomTypography fontSize={14}>
                          Unit Price
                        </CustomTypography>
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: colors.tableHeader,
                          borderRight: `1px solid ${colors.border}`,
                          width: "25%",
                          border: `1px solid ${colors.border}`,
                        }}
                      >
                        <CustomTypography fontSize={14}>Price</CustomTypography>
                      </TableCell>
                      {edit && <TableCell />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(fetchedOrder?.line_items || [])
                      .filter((xx) => !xx.child)
                      .map((item, index) => (
                        <TableRow key={item._id + "order-details" + index}>
                          <TableCell
                            style={{
                              borderRight: `1px solid ${colors.border}`,
                              border: `1px solid ${colors.border}`,
                              maxWidth: 350,
                            }}
                          >
                            {editingItem?.index === index &&
                            editingItem?.field === "name" ? (
                              <TextField
                                value={editedValues.name || item.name}
                                onChange={(e) =>
                                  handleInputChange("name", e.target.value)
                                }
                                onBlur={() => handleSave(index)}
                                autoFocus
                                fullWidth
                                variant="outlined"
                                size="small"
                              />
                            ) : (
                              <>
                                <CustomTypography multiline fontSize={14}>
                                  {item.name}
                                </CustomTypography>
                                <CustomTypography
                                  multiline
                                  fontSize={14}
                                  color="#888"
                                >
                                  {`(${item.sku})`}
                                </CustomTypography>

                                {item.bundle ? (
                                  <CustomTypography style={{ marginTop: 1 }}>
                                    {(displayOrder?.line_items || [])
                                      .filter((xx) => xx.parent === item.uid)
                                      .map((bundleItem, index) => (
                                        <CustomTypography
                                          fontSize={13}
                                          key={bundleItem._id}
                                        >
                                          {++index +
                                            ") " +
                                            bundleItem.sku +
                                            " * " +
                                            bundleItem.quantity}
                                        </CustomTypography>
                                      ))}
                                  </CustomTypography>
                                ) : (
                                  <></>
                                )}
                                {edit && (
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleEditClick(index, "name")
                                    }
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </>
                            )}
                          </TableCell>
                          <TableCell
                            style={{
                              borderRight: `1px solid ${colors.border}`,
                              border: `1px solid ${colors.border}`,
                            }}
                          >
                            {editingItem?.index === index &&
                            editingItem?.field === "quantity" ? (
                              <TextField
                                value={editedValues.quantity || item.quantity}
                                onChange={(e) =>
                                  handleInputChange("quantity", e.target.value)
                                }
                                onBlur={() => handleSave(index)}
                                autoFocus
                                fullWidth
                                variant="outlined"
                                size="small"
                              />
                            ) : (
                              <>
                                <CustomTypography fontSize={14}>
                                  {item.quantity}
                                </CustomTypography>
                                {edit && (
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleEditClick(index, "quantity")
                                    }
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </>
                            )}
                          </TableCell>
                          <TableCell
                            style={{
                              borderRight: `1px solid ${colors.border}`,
                              border: `1px solid ${colors.border}`,
                            }}
                          >
                            <CustomTypography fontSize={14}>
                              {item.price
                                ? `$${parseFloat(item.price).toLocaleString("en-US")}`
                                : item.price}
                            </CustomTypography>
                          </TableCell>
                          <TableCell
                            style={{
                              borderRight: `1px solid ${colors.border}`,
                              border: `1px solid ${colors.border}`,
                            }}
                          >
                            <CustomTypography fontSize={14}>
                              {item.price
                                ? `$${(Number(item.price) * Number(item.quantity)).toLocaleString("en-US")}`
                                : Number(item.price) * Number(item.quantity)}
                            </CustomTypography>
                          </TableCell>
                          {edit && (
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(index)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions style={{ padding: 10 }}>
        <Button
          onClick={onClose}
          variant="contained"
          style={{ textTransform: "none" }}
          color="primary"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailsModal;
